import React, { useEffect, useState, Suspense } from 'react'
import smoothscroll from 'smoothscroll-polyfill'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import VotationResultsTable from 'components/votationResultsTable'
import { VoteBackendResponse } from 'types/voteBackendResponse'
import csvjson from 'csvjson'
import styles from './styles.m.scss'
import { Scrollchor } from 'react-scrollchor'
import { FetchErrorMessage } from 'components/fetchErrorMessage'
import ReactHtmlParser from 'react-html-parser'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'
import { useLocales } from 'hooks/useLocales'
import { CopyToClipboardUrl } from 'components/copyToClipboardUrl'
import { ArticleTeaser } from 'components/articleLink'
import { useStore } from 'hooks/useStore'

import './styles.scss'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/placeholder.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'

smoothscroll.polyfill()

type ArticleType = {
  bfs: string | null
  date: string
  lang: 'de' | 'fr'
  format: string
}

export const Article = ({ bfs, date, lang, format }: ArticleType) => {
  const [communalVoteResults, setCommunalVoteResults] = useState(null)
  const [texts, setTexts] = useState(null)
  const [modules, setModules] = useState(null)
  const [title, setTitle] = useState(null)
  const [lastUpdateDate, setLastUpdateDate] = useState(null)
  const [errorStatus, setErrorStatus] = useState(false)
  const { locale, messages } = useLocales()
  const { setUrlParams } = useStore()

  /**
   * Retrieves processed communal vote results
   *
   * @param  bfs Commune UID
   * @param locale
   * @param date
   */
  const getCommunalVoteResultText = (
    bfs: string,
    locale: 'de' | 'fr',
    date: string
  ) => {
    const BASE_URL =
      process.env.NODE_ENV === 'production'
        ? 'https://atm.tamedia.ch'
        : 'http://localhost:5000'
    // const BASE_URL = 'https://atm.tamedia.ch'
    const API_ENDPOINT = '/api/vote/get_communal_results'
    const endpoint = new URL(API_ENDPOINT, BASE_URL)
    endpoint.searchParams.append('date', date) // 20200927
    endpoint.searchParams.append('lang', locale)
    endpoint.searchParams.append('bfsid', bfs)
    endpoint.searchParams.append('format', 'text')
    const url = endpoint

    // noinspection JSIgnoredPromiseFromCall
    // Article.sendToDataPipeline(bfs)

    return window
      .fetch(url.href, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json() as Promise<VoteBackendResponse>
        }
        if (response.status === 404) {
          return new Promise(resolve => {
            resolve({ content: null })
          })
        }
        throw new Error(response.status + ': ' + response.statusText)
      })
      .catch(error => {
        if (error.message.startsWith('Network')) {
          error.message = '500: ' + error.message
        }
        // this is a catch all, if municipality content cannot be loaded for any internal server reason, it is handled like a 404
        return new Promise(resolve => {
          resolve({ content: null })
        })
      })
  }

  /**
   * Retrieves processed texts from initial wordsmith templates
   *
   * @param  bfs Commune UID
   * @param locale
   */
  const getVoteText = (bfs: string, locale: 'de' | 'fr', date) => {
    const BASE_URL =
      process.env.NODE_ENV === 'production'
        ? 'https://atm.tamedia.ch'
        : 'http://localhost:5000'
    // const BASE_URL = 'https://atm.tamedia.ch'
    const API_ENDPOINT = '/api/vote/get_raw_text'
    const endpoint = new URL(API_ENDPOINT, BASE_URL)
    endpoint.searchParams.append('date', date) // 20200927
    endpoint.searchParams.append('lang', locale)
    endpoint.searchParams.append('bfsid', bfs)
    const url = endpoint

    // noinspection JSIgnoredPromiseFromCall
    // Article.sendToDataPipeline(bfs)

    return window
      .fetch(url.href, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json() as Promise<VoteBackendResponse>
        }
        throw new Error(response.status + ': ' + response.statusText)
      })
      .catch(error => {
        if (error.message.startsWith('Network')) {
          error.message = '500: ' + error.message
        }
        throw new Error(error.message)
      })
  }

  const getVoteInfoDataCsv = () => {
    return window
      .fetch(`https://atm-vote.s3.amazonaws.com/chartdata_${date}.csv`)
      .then(response => {
        if (!response.ok) {
          console.log(
            'No data for charts yet:' +
              response.status +
              ': ' +
              response.statusText
          )
        }
        return response.text()
      })
      .then(csv => {
        const csvData = csvjson.toObject(csv, {})
        const data = new Array<{ objectNumber: number; data: Municipality[] }>()
        const objects = new Array<{ objectNumber: number; key: string }>()

        Object.keys(csvData[0]).forEach(k => {
          const match = k.match(/(\d)_jastimmeninprozent/)
          if (match) {
            objects.push({
              objectNumber: +match[1],
              key: k
            })
          }
        })

        objects.forEach(o => {
          data.push({
            objectNumber: o.objectNumber,
            data: csvData.reduce((a, b) => {
              const res = +b[o.key]
              if (res) {
                return [
                  ...a,
                  {
                    canton: b.gdekt,
                    municipalityNumber: +b.bfs,
                    municipalityName: b[`gdename_${lang}`],
                    result: +b[o.key]
                  }
                ]
              } else return a
            }, [])
          })
        })

        return new Promise(resolve => {
          resolve(data)
        }) as Promise<{ objectNumber: number; data: Municipality[] }[]>
      })
      .catch(error => {
        if (error.message.startsWith('Network')) {
          error.message = '500: ' + error.message
        }
        throw new Error(error.message)
      })
  }

  const handleError = (error: Error) => {
    const status = parseInt(error.message.split(':')[0])
    setErrorStatus(status)
    console.error('Problem with loading data: ' + error.message)
  }

  /**
   * Find the title from the received text
   * @param text
   */
  const extractTitle = (text: string) => {
    const titleRe = /<h4.*?>(.*?)(<br>|<\/h4>)/g
    const match = titleRe.exec(text)
    if (match && match[1]) {
      return match[1]
    } else {
      return messages.title
    }
  }

  /**
   * Find the update date from the received text
   * @param text
   */
  const extractLastUpdated = (text: string) => {
    const lastUpdatedRe = /<small.*?>(.*?)(<br>|<\/small>)/g
    const match = lastUpdatedRe.exec(text)
    if (match && match[1]) {
      return match[1]
    } else {
      return null
    }
  }

  /**
   * Clean up the received text
   * @param text
   */
  const cleanText = (text: string) => {
    // Remove the "small" tags used for the update date
    text = text.replace(/<small.*?>(.*?)(<br>|<\/small>(<br>)*)/g, '')

    // Remove the HTML comments
    text = text.replaceAll(/(?=<!--)([\s\S]*?)-->/g, '')

    // remove the paragraph tags
    text = text.replaceAll('<p>', '<br><br>')
    text = text.replaceAll('</p>', '')

    // remove the spaces between the breaklines
    text = text.replace(/\s*<br>\s*/g, '<br>')

    // Remove the breaklines at the beginning and end of the text
    text = text.replace(/^\s*(<br>\s*)+|(<br>\s*)+\s*$/g, '')

    // get rid of the empty texts
    text = text.replace(/^(<br>)*$/g, '')

    if (text.length === 0 || new RegExp(/^\s*$/g).test(text) === true) {
      return ' '
    }

    // Remove the headers
    text = text.replace(/<h4.*?>(.*?)(<br>|<\/h4>(<br>)*)/g, '')
    text = text.replace(/<h5.*?>(.*?)(<br>|<\/h5>(<br>)*)/g, '')

    // Double breaklines are replaced by a paragraph, reconstructs the paragraphs
    text = text.replace(/(.*?)<br>(<br>)+/g, '$1</p><p>')
    text = text.replace(/(<h4.*?<\/h4>)+/g, '$1<p>')
    text = '<p>' + text + '</p>'
    text = text.replace(/<\/p>+/g, '</p>')
    text = text.replace(/<p>(<h.*?>)/g, '$1')
    text = text.replace(/(<\/h\d>)<\/p>/g, '$1')
    text = text.replace(/<p>\s*?<br>/, '<p>')

    return text
  }

  /**
   * Split the received text according to custom tags.
   * Also need the results to construct the chart module.
   * @param text
   * @param results
   */
  const splitText = (
    text: string,
    results: Array<{ objectNumber: number; data: Municipality[] }>
  ) => {
    // Extract the results table
    const reTable = new RegExp(/{.*}/, 'gmi')
    const table = text.match(reTable)
    const rawText = text.replace(reTable, '')

    // Find the modules to add in the text
    const modulesMatchResult = rawText.match(/#MODULE:.*?#/gi)
    const modules = new Array<{ type: string; level: string; element: JSX.Element }>()

    // If there are modules tags in the text
    if (modulesMatchResult) {
      modulesMatchResult.forEach(t => {
        // Find the type of module
        const typeMatch = t.match(/#MODULE:(.*)?\(/)

        if (typeMatch) {
          const params = t.match(/\((.*?)\)/)
          let paramsList = new Array<string>()
          const type = typeMatch[1].toLowerCase()

          if (params) {
            paramsList = params[1].split(',')
            paramsList[0] = paramsList[0].toLowerCase()
          }

          const objectData = results.find(
            o => o.objectNumber === +paramsList[1]
          )
          // Add a new module according to the type
          switch (type) {
            case 'table':
              // If we don't have the data, then don't add the table
              if (table) {
                modules.push({
                  type: 'table',
                  level: paramsList[0],
                  element: (
                    <VotationResultsTable
                      chartData={objectData ? objectData.data : null}
                      municipalityNumber={bfs}
                      table={table[0]}
                      showOnly={paramsList}
                      lang={lang}
                    />
                  )
                })
              }
              break
            case 'teasers':
              modules.push({
                type: 'teasers',
                level: 'federal',
                element: <ArticleTeaser locale={locale} ids={paramsList} />
              })
              break
          }
        }
      })
    }
    // Split the text into the same number of modules it contains
    // We want to show a module after each of these text sections
    let cantonalTexts = []
    const t = rawText.split('#CANTON#')
    const federalTexts = t[0].split(/#MODULE.*?#/) || []
    const communalText =
      rawText.split(`<h5>${messages.blockTitleLocal}</h5>`)[1] || null

    // If there are cantonal texts
    if (t.length > 1) {
      cantonalTexts = t[1].split(/#MODULE.*?#/)
      cantonalTexts.forEach((t, i) => {
        // Find the title
        let title = null
        const titleRe = /<h5.*?>(.*?)(<br>|<\/h5>)/g
        const match = titleRe.exec(t)
        if (match && match[1]) {
          title = match[1]
        }

        // Add the text bloc to the cantonal list
        cantonalTexts[i] = {
          title: title,
          text: cleanText(t)
        }
      })
    }
    // remove possible communal texts from cantonal list
    cantonalTexts = cantonalTexts.filter(obj => obj.title !== messages.blockTitleLocal)
    // Clean the text by replacing <br> by paragraphs and removing unnecessary breaklines
    federalTexts.forEach((t, i) => {
      federalTexts[i] = cleanText(t)
    })

    return {
      texts: {
        federal: federalTexts,
        cantonal: cantonalTexts,
        communal: communalText && cleanText(communalText)
      },
      modules: modules,
      rawText: rawText
    }
  }

  useEffect(() => {
    const formatedDate = date.replaceAll('-', '')
    const backendRawText = getVoteText(bfs, lang, formatedDate)
    const communalVoteResultText = getCommunalVoteResultText(
      bfs,
      lang,
      formatedDate
    )
    const officialResults = getVoteInfoDataCsv()

    setTexts(null)

    Promise.all([backendRawText, officialResults, communalVoteResultText])
      .then(data => {
        // Split the text according to its custom tags
        const splittedText = splitText(data[0].content, data[1])

        // Find the title
        const textTitle = extractTitle(data[0].content)

        // Find the last update date
        const lastUpdateDate = extractLastUpdated(data[0].content)

        // Split the communal vote results text into object blocks
        let communalVoteText = data[2].content
        let communalVoteTextBlocks = []

        if (communalVoteText) {
          communalVoteText = communalVoteText.replaceAll(/^<ul><li>/g, '')
          communalVoteText = communalVoteText.replaceAll(/<\/ul><\/li>$/g, '')

          communalVoteText.split('</li><li>').forEach((t, i) => {
            communalVoteTextBlocks = [
              ...communalVoteTextBlocks,
              ...t.split('</li></ul><li>')
            ]
          })
        }

        // Set the content
        setTitle(textTitle)
        setLastUpdateDate(lastUpdateDate)
        setTexts(splittedText.texts)
        setModules(splittedText.modules)
        setCommunalVoteResults(communalVoteTextBlocks)

        // Everything has been retrieved
        setErrorStatus(null)
      })
      .catch(e => handleError(e))
  }, [bfs, date, lang])

  useEffect(() => {
    // If the texts change, then we need to the top of the page
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth'
    })
  }, [texts])

  // Used to keep track of the texts and modules index
  let federalTextSliceIndex = 0;
  let cantonalTextSliceIndex = 0;
  const menuItems = [
    <div
      key='menu-item-0'
      className={`${styles.menuItem} ${styles.menuItemActive}`}
    >
      Accueil
    </div>,
    <div className={`${styles.menuItem}`} key='menu-item-1'>
      <Scrollchor to='#cantonal-votes'>{messages.blockTitleFederal}</Scrollchor>
    </div>,
    <div className={`${styles.menuItem}`} key='menu-item-3'>
      <Scrollchor to='#cantonal-votes'>{messages.gotoCanton}</Scrollchor>
    </div>
  ]
  let federalModules = null;
  let cantonalModules = null;

  if (modules && modules.length > 0) {
    federalModules = modules.filter(module => module.level === 'federal' || module.level === 'participation');
    cantonalModules = modules.filter(module => module.level === 'cantonal');
  }
  // Setup content for the default case
  let Content = (
    <div className={styles.railContainer}>
      {/* <div className='ui container'>
        <div
          className='ui primary button'
          onClick={() => {
            setUrlParams({
              date: null,
              bfs: null
            })
          }}
        >
          <i className='ui chevron left icon' /> {messages.backToMain}
        </div>
      </div> */}
      {texts ? (
        <>
          {/* If there are only federal votes */}
          {texts.federal && (
            <div style={{ marginTop: '1em' }}>
              <div
                className='ui ta text container'
                style={{ position: 'relative' }}
              >
                <aside
                  className={`${styles.rail} ${styles.railLeft}`}
                >
                  <div>
                    <div
                      className='ui primary button'
                      style={{ marginBottom: '.5em' }}
                      onClick={() => {
                        // Do the people actually use this button?
                        window.dataLayer = window.dataLayer || []
                        window.dataLayer.push({
                          event: 'Interactions',
                          event_action: 'click',
                          event_label: `nav:homepage`
                        })
                        setUrlParams({
                          date: null,
                          bfs: null
                        })
                      }}
                    >
                      <i className='ui chevron left icon' />{' '}
                      {messages.backToMain}
                    </div>
                  </div>

                  {/* If there are federal and communal votes, show a mini
              menu that leads to the cantonal votes. */}
                  {texts.cantonal && texts.cantonal.length > 0 && (
                    <div
                      className='ui primary button'
                      style={{ marginTop: '0.2em' }}
                    >
                      {/* <div className='ui divider' /> */}
                      <Scrollchor
                        to='#cantonal-votes'
                        beforeAnimate={() => {
                          // Do the people actually use this menu?
                          window.dataLayer = window.dataLayer || []
                          window.dataLayer.push({
                            event: 'Interactions',
                            event_action: 'click',
                            event_label: `jump:cantons`
                          })
                        }}
                      >
                        {messages.gotoCanton}
                        <i className='ui icon chevron right' />
                      </Scrollchor>
                      {/* <div className='ui divider' /> */}
                    </div>
                  )}
                  {((texts.communal && texts.communal.length) > 0 || (communalVoteResults && communalVoteResults.length > 0)) && (
                    <div
                      className='ui primary button'
                      style={{ marginTop: '0.2em' }}
                    >
                      {/* <div className='ui divider' /> */}
                      <Scrollchor
                        to='#communal-votes'
                        beforeAnimate={() => {
                          // Do the people actually use this menu?
                          window.dataLayer = window.dataLayer || []
                          window.dataLayer.push({
                            event: 'Interactions',
                            event_action: 'click',
                            event_label: `jump:communal`
                          })
                        }}
                      >
                        {messages.communalVotesTitle}
                        <i className='ui icon chevron right' />
                      </Scrollchor>
                      {/* <div className='ui divider' /> */}
                    </div>
                  )}
                </aside>

                {/* Show the section title */}
                {!['v1', 'v2'].includes(format) && (
                  <h4 className='section-title'>
                    {messages.blockTitleFederal}
                  </h4>
                )}

                {/* Show each vote object as a block */}
                <div id='federal-votes'>
                  {texts.federal && texts.federal.map((t, i) => {
                    if (texts.federal.length > 1 && modules.length > 0) {
                      if (i < texts.federal.length - 1) {
                        federalTextSliceIndex += 1
                      } else return false
                    } else federalTextSliceIndex = 1
                    if (t) {
                      return (
                        <div
                          key={'federal-results-' + i}
                          className={`ui raised segment text-block ${federalModules && federalModules[
                            federalTextSliceIndex - 1
                          ] && federalModules[federalTextSliceIndex - 1].type}-block`}
                        >
                          {/* Show the text */}
                          {ReactHtmlParser(t)}

                          {/* Show the module (table & chart or teasers) */}
                          {federalModules && i < texts.federal.length - 1 &&
                            federalModules[federalTextSliceIndex - 1].element}
                        </div>
                      )
                    }
                  })}
                </div>

                {/* Link to the votations dashboard */}
                <aside
                  className={`${styles.rail} ${
                    texts.cantonal.length <= 1 && texts.federal.length <= 1
                      ? styles.railMobile
                      : ''
                  }`}
                >
                  <a
                    className={styles.dashboardLink}
                    href={`https://${messages.dashboardPrefix}.${
                      parseHostname().publicationDomain
                    }/`}
                  >
                    <img alt='' src={require('./votationHub.jpg')} />
                    <div className={styles.dashboardLinkContent}>
                      <span className='abo'>Abo</span>
                      <small>{messages.votationLinkSup}</small>
                      <div>{messages.votationLink}</div>
                    </div>
                  </a>
                </aside>
              </div>
            </div>
          )}

          {/* Show the sharing component */}
          <div className='ui vertical segment' style={{ paddingBottom: '0' }}>
            <div className='ui ta text container'>
              <CopyToClipboardUrl
                locale={locale}
                title={messages.shareUrlTitle}
                text={messages.shareUrlText}
                url={window.location.href}
                buttonColor='#202346'
              />
            </div>
          </div>

          {/* If there are cantonal votes */}
          {texts.cantonal && texts.cantonal.length > 0 && (
            <>
              <div className='section-separator' />
              <div className='ui ta text container'>
                <div id='cantonal-votes'>
                  {texts.cantonal.map((t, i) => {
                    cantonalTextSliceIndex += 1

                    // Display a block only if there is a text or a module to show
                    if (t.text.length > 1) {
                      return (
                        <>
                          {t.title && (
                            <h4 className='section-title'>{t.title}</h4>
                          )}
                          <div
                            key={'cantonal-results-' + i}
                            className={`ui raised segment text-block ${cantonalModules && cantonalModules[
                              cantonalTextSliceIndex - 1
                            ] && cantonalModules[cantonalTextSliceIndex - 1].type}-block`}
                          >
                            {/* Show the text */}
                            {ReactHtmlParser(t.text)}

                            {/* Show the module (table & chart or teasers) */}
                            {cantonalModules && cantonalModules[cantonalTextSliceIndex - 1] && i < texts.cantonal.length &&
                              cantonalModules[cantonalTextSliceIndex - 1].element}
                          </div>
                        </>
                      )
                    }
                  })}
                </div>
              </div>
            </>
          )}

          {/* If there are communal votes */}
          {communalVoteResults && communalVoteResults.length > 0 && (
            <>
              <div className='section-separator' />
              <div className='ui ta text container'>
                <div id='communal-votes'>
                  <h4 className='section-title'>{messages.communalVotesTitle}</h4>
                  {communalVoteResults.map((blockContent, i) => (
                    <div
                      key={`communal-vote-result-${i}`}
                      className='ui raised segment text-block'
                    >
                      <p
                        className='text-block'
                        dangerouslySetInnerHTML={{ __html: blockContent }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {texts.communal && (
            <div>
              <div className='section-separator' />
              <div className='ui ta text container'>
                <div id='communal-votes'>
                  <h4 className='section-title'>{messages.blockTitleLocal}</h4>

                  <div
                    key='communal-vote-text'
                    className='ui raised segment text-block'
                  >
                    <p
                      className='text-block'
                      dangerouslySetInnerHTML={{ __html: texts.communal }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* only show feedback prompt when federal texts are available */}
          {texts.federal && texts.federal.length > 1 && (
            <div className='ui ta text container' style={{ marginTop: '1em' }}>
              <FeedbackMessage locale={lang} mailTo='atm@tamedia.ch' />
            </div>
          )}
        </>
      ) : (
        // Loading placeholder
        <div className='ui ta text container'>
          <div className='ui placeholder'>
            <div className='header'>
              <div className='line' />
              <div className='line' />
            </div>
            <div className='paragraph'>
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
            </div>
            <div className='paragraph'>
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
              <div className='line' />
            </div>
            <div className='paragraph'>
              <div className='line' />
              <div className='line' />
              <div className='line' />
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // show something else in case of an error
  if (errorStatus) {
    Content = (
      <div className='ui text container'>
        <FetchErrorMessage status={errorStatus ?? ''} locale={lang} />
      </div>
    )
  }

  return (
    <>
      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>{title}</h1>
            <h2>{messages.lead}</h2>
          </div>
          {/* Last updated date */}
          {lastUpdateDate && (
            <small
              className='ta text'
              style={{
                fontFamily: 'TamediaUI',
                display: 'inline-block'
              }}
            >
              {lastUpdateDate}
            </small>
          )}
        </div>
      </header>
      <div className={styles.menu}>
        <div className='ui container'>{menuItems.map(item => item)}</div>
      </div>
      <div
        className='ui vertical fitted segment'
        // id='main-content'
        style={{
          backgroundColor: 'var(--color-gray-gray-6)',
          borderTop: '1px solid #d5d6d7',
          marginTop: '1em'
        }}
      >
        <div className='step article' style={{ zIndex: 10 }}>
          {Content}
        </div>
      </div>
    </>
  )
}
